import { getApiBase, getAuthHeaders } from '../config/api';

/**
 * API Client với xử lý tự động đăng xuất khi token hết hạn
 */
class ApiClient {
  private baseUrl: string;
  private onTokenExpired?: () => void;

  constructor() {
    this.baseUrl = getApiBase();
  }

  /**
   * Thiết lập callback khi token hết hạn
   */
  setTokenExpiredCallback(callback: () => void) {
    this.onTokenExpired = callback;
  }

  /**
   * Thực hiện request với xử lý lỗi 401
   */
  async request(url: string, options: RequestInit = {}): Promise<Response> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    
    // Thêm headers xác thực nếu chưa có
    let headers = {
      ...getAuthHeaders(),
      ...options.headers,
    };

    // Nếu body là FormData, không set Content-Type để browser tự động set
    if (options.body instanceof FormData) {
      const { 'Content-Type': _, ...headersWithoutContentType } = headers as any;
      headers = headersWithoutContentType;
    }

    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    // Kiểm tra nếu token hết hạn hoặc tài khoản bị khóa (401 Unauthorized)
    if (response.status === 401) {
      // Thử lấy thông báo lỗi từ response
      let errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
      
      try {
        const errorData = await response.clone().json();
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Nếu không parse được JSON, sử dụng thông báo mặc định
      }
      
      
      // Xóa token khỏi localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('auth');
      
      // Gọi callback đăng xuất nếu có
      if (this.onTokenExpired) {
        this.onTokenExpired();
      }
      
      // Redirect về trang đăng nhập
      window.location.href = '/dang-nhap';
      
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * GET request
   */
  async get(url: string, options: RequestInit = {}): Promise<Response> {
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post(url: string, data?: any, options: RequestInit = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put(url: string, data?: any, options: RequestInit = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch(url: string, data?: any, options: RequestInit = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete(url: string, options: RequestInit = {}): Promise<Response> {
    return this.request(url, { ...options, method: 'DELETE' });
  }
}

// Tạo instance singleton
export const apiClient = new ApiClient();
